import { Database02Icon } from '@langchain/untitled-ui-icons';
import { Autocomplete, AutocompleteOption } from '@mui/joy';
import Box from '@mui/joy/Box';
import FormLabel from '@mui/joy/FormLabel';

import { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';

import { useDataset, useDatasets } from '../hooks/useSwr';
import {
  DatasetSchema,
  GetDatasetsQueryParams,
  RunSchema,
} from '../types/schema';
import { getDataTypeTitle } from '../utils/get-data-type-title';
import { DatasetCrudPaneWithButton } from './DatasetCrudPane/DatasetCrudPaneWithButton';
import { ShowAllPickerExtraItem } from './ResourceTagFilter/ShowAllPickerExtraItem';
import { TruncatedTextWithTooltip } from './TruncatedTextWithTooltip.tsx';

export function DatasetPicker({
  label,
  filter,
  onChange,
  datasetId,
  prefilledData,
  placeholder,
  autoFocus = false,
  withNewButton = true,
  size = 'md',
  className,
  preselectedRuns,
  baseDatasetOnRunType,
}: {
  label?: string | null;
  filter?: GetDatasetsQueryParams;
  onChange: (datasetId: string | undefined) => void;
  datasetId: string | undefined;
  autoFocus?: boolean;
  withNewButton?: boolean;
  placeholder?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  prefilledData?: Partial<DatasetSchema>;
  preselectedRuns?: Pick<RunSchema, 'inputs' | 'outputs' | 'extra' | 'id'>[];
  baseDatasetOnRunType?: RunSchema['run_type'];
}) {
  const [searchInput, setSearchInput] = useState('');
  const [showAll, setShowAll] = useState(false);
  const { selectedTagIds } = useStoredResourceTags();

  const [debouncedSearchInput] = useDebounce(searchInput, 300);
  const datasets = useDatasets({
    ...filter,
    ...{ name_contains: debouncedSearchInput },
    tag_value_id: showAll ? undefined : selectedTagIds,
    exclude_corrections_datasets: true,
  });

  const labelWithDefault = label !== null ? label ?? 'Dataset' : null;

  const currentDataset = useDataset(datasetId ?? undefined);
  const currentDatasetName = currentDataset.data?.name;

  useEffect(() => {
    if (datasetId === null) {
      setSearchInput('');
    } else if (currentDatasetName && !currentDataset.isLoading) {
      setSearchInput(currentDatasetName);
    }
  }, [currentDatasetName, datasetId, currentDataset.isLoading]);

  const newDatasetButton = (
    <DatasetCrudPaneWithButton
      dataType={
        filter?.data_type
          ? [
              ...(Array.isArray(filter.data_type)
                ? filter.data_type
                : [filter.data_type]),
              'kv',
            ]
          : undefined
      }
      onSuccess={(id, dataset) => {
        setSearchInput(dataset.name);
        onChange(id ?? undefined);
      }}
      asLink
      prefilledData={prefilledData}
      preselectedRuns={preselectedRuns}
      baseDatasetOnRunType={baseDatasetOnRunType}
    />
  );

  return (
    datasets.data && (
      <>
        {labelWithDefault || withNewButton ? (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            sx={{ marginBottom: 0.5 }}
          >
            {labelWithDefault != null && (
              <FormLabel>{labelWithDefault}</FormLabel>
            )}
            {withNewButton && labelWithDefault != null && newDatasetButton}
          </Box>
        ) : null}
        <Autocomplete
          autoFocus={autoFocus}
          data-testid="dataset-picker"
          placeholder={placeholder ?? 'Choose one…'}
          onChange={(event, value, reason) => {
            if (reason === 'clear' && event.type === 'click') {
              onChange(undefined);
            } else if (value) {
              onChange(value);
            }
          }}
          // xs styling is taken care of in the sx prop
          size={size === 'xs' ? 'sm' : size}
          className={className}
          onInputChange={(_, value) => {
            setSearchInput(value);
          }}
          startDecorator={<Database02Icon className="h-4 w-4" />}
          clearOnBlur={false}
          value={datasets.data.find((d) => d.id === datasetId)?.id ?? null}
          inputValue={searchInput}
          options={[...datasets.data.map((d) => d.id), undefined]}
          renderOption={(props, dataset) => {
            if (!dataset) {
              return (
                <AutocompleteOption
                  key={'show-all'}
                  variant="plain"
                  sx={{ display: 'flex', alignItems: 'center' }}
                >
                  <ShowAllPickerExtraItem
                    showAll={showAll}
                    resources={datasets.data}
                    setShowAll={setShowAll}
                    hasSelectedTags={selectedTagIds.length > 0}
                  />
                </AutocompleteOption>
              );
            }
            const currDataset = datasets?.data?.find((d) => d.id === dataset);
            return (
              <AutocompleteOption {...props} key={dataset}>
                <div className="flex w-full flex-row justify-between gap-0.5">
                  <div className="flex flex-col gap-0.5 overflow-hidden">
                    <TruncatedTextWithTooltip text={currDataset?.name} />
                    <span className="text-xs text-tertiary">
                      ({currDataset?.example_count}{' '}
                      {currDataset?.example_count === 1
                        ? 'example'
                        : 'examples'}
                      )
                    </span>
                  </div>
                  {currDataset?.data_type !== 'kv' && (
                    <div className="my-auto whitespace-nowrap rounded-full border border-secondary bg-tertiary px-1.5 py-1 text-xxs">
                      {currDataset?.data_type &&
                        getDataTypeTitle(currDataset.data_type)}
                    </div>
                  )}
                </div>
              </AutocompleteOption>
            );
          }}
          getOptionLabel={(value) => {
            const currDataset = datasets?.data?.find((d) => d.id === value);
            return currDataset?.name ?? '';
          }}
          sx={
            size == 'xs'
              ? {
                  '--Input-minHeight': '30px',
                  '& .MuiAutocomplete-root': {
                    height: '30px',
                    padding: 0,
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    height: '30px',
                    padding: 0,
                  },
                  '& .MuiInputBase-root': {
                    height: '30px',
                    padding: 0,
                  },
                  '& .MuiInputBase-input': {
                    height: '30px',
                    padding: 0,
                    lineHeight: '30px',
                  },
                  '& .MuiAutocomplete-startDecorator': {
                    height: '30px',
                  },
                  '& .MuiAutocomplete-popupIndicator': {
                    height: '24px',
                  },
                  '& .MuiAutocomplete-clearIndicator': {
                    height: '24px',
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    margin: 0,
                  },
                  '& .MuiAutocomplete-wrapper': {
                    margin: 0,
                    padding: 0,
                  },
                }
              : {}
          }
        />

        {withNewButton && labelWithDefault == null && newDatasetButton}
      </>
    )
  );
}
